import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import Gallery from '../components/Gallery.jsx';
import MapPanel from '../components/MapPanel.jsx';
import ParcelMap, { plotSize } from '../components/ParcelMap.jsx';
import PropertyCard from '../components/PropertyCard.jsx';
import { Avatar, ErrorState, Field, Modal, Spinner, StatusBadge } from '../components/ui.jsx';
import { api, ApiError } from '../api/client.js';
import { useFetch, useTitle } from '../lib/hooks.js';
import { formatDate, formatNumber, formatXaf, timeAgo } from '../lib/format.js';
import { useAuth } from '../state/AuthContext.jsx';
import { useMeta } from '../state/MetaContext.jsx';
import { useToast } from '../state/ToastContext.jsx';
import { AVAILABILITY_LABEL, formatPlot, isForSale, isMultiUnit, lettingOf, statusBadgeFor } from '../lib/letting.js';

// Where a unit stands, whether the payload carries the public word for it or,
// for the owner, the unit's own status.
const UNIT_AVAILABILITY = { vacant: 'available', reserved: 'pending', occupied: 'occupied', maintenance: 'unavailable' };
const unitAvailability = (unit) => unit.availability || UNIT_AVAILABILITY[unit.status] || 'unavailable';

const ApplyModal = ({ property, initialUnitId = null, onClose, onDone }) => {
  const toast = useToast();
  const { documentKinds } = useMeta();
  const { user } = useAuth();
  // Nobody is made to register before they can ask about a house. A visitor gives
  // their name, email and number here instead, and only gets an account if the
  // landlord approves them.
  const guest = !user;

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    coverMessage: '',
    occupants: 1,
    moveInDate: '',
    monthlyIncome: '',
    employment: ''
  });
  const [selectedUnitId, setSelectedUnitId] = useState(initialUnitId);
  const [documents, setDocuments] = useState([]);
  const [documentKind, setDocumentKind] = useState('id_card');
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  // Set once a guest has applied: the modal then shows their reference instead of
  // the form, because it is the one thing they have to keep.
  const [sent, setSent] = useState(null);
  const selectedUnit = property.units?.find((unit) => String(unit.id) === String(selectedUnitId)) ?? null;
  const words = lettingOf(property);
  const choosing = isMultiUnit(property) && property.units?.length > 0;
  const freeUnits = (property.units || []).filter((unit) => unitAvailability(unit) === 'available');

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const submit = async (event) => {
    event.preventDefault();
    if (choosing && !selectedUnit) {
      setErrors({ unitId: `Pick the ${words.unit} you want.` });
      return;
    }
    setBusy(true);
    setErrors({});

    try {
      const payload = {
        unitId: selectedUnitId ? Number(selectedUnitId) : undefined,
        coverMessage: form.coverMessage || undefined,
        occupants: Number(form.occupants) || 1,
        moveInDate: form.moveInDate || undefined,
        monthlyIncome: form.monthlyIncome ? Number(form.monthlyIncome) : undefined,
        employment: form.employment || undefined
      };

      if (guest) {
        const result = await api.post(`/properties/${property.id}/applications/guest`, {
          ...payload,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim()
        });
        setSent(result);
        setBusy(false);
        return;
      }

      const { application } = await api.post(`/properties/${property.id}/applications`, payload);

      // Documents go up after the application exists, so a failed upload does not
      // lose the form the tenant just filled in.
      if (documents.length > 0) {
        const formData = new FormData();
        formData.append('kind', documentKind);
        for (const file of documents) formData.append('files', file);
        try {
          await api.upload(`/applications/${application.id}/documents`, formData);
        } catch (error) {
          toast.error(`Application sent, but the documents did not upload: ${error.message}`);
        }
      }

      toast.success('Application sent. You will get a notification when the landlord replies.');
      onDone(application);
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  // What a guest sees the moment their application lands. The reference is the only
  // way back to it until the landlord decides, so it gets the whole dialog.
  if (sent) {
    return (
      <Modal
        title="Your application is in"
        onClose={onClose}
        footer={
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        }
      >
        <p className="modal-lead">
          {selectedUnit ? `${property.title} - ${selectedUnit.label}` : property.title} - the landlord has been notified and can see your details.
        </p>

        <div className="reference-callout">
          <span>Your reference</span>
          <strong>{sent.reference}</strong>
        </div>

        <p>
          Keep this. You can check where your application stands at any time on the{' '}
          <Link to="/track">track an application</Link> page, using that reference and{' '}
          <strong>{form.email.trim()}</strong>. We have emailed it to you as well.
        </p>
        <p className="muted">
          You do not have an account yet, and you do not need one. If the landlord approves you we will
          email you a username and a temporary password so you can sign the lease, pay your rent and
          print your receipts here.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title={`Apply for ${property.title}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="apply-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Sending...' : 'Send application'}
          </button>
        </>
      }
    >
      <p className="modal-lead">
        {selectedUnit ? (
          <>
            You are applying for <strong>{selectedUnit.label}</strong> in {property.title}. The rent for this{' '}
            {words.unit || 'unit'} is{' '}
            {formatXaf(selectedUnit.rent)} a month with {selectedUnit.depositMonths} month
            {selectedUnit.depositMonths === 1 ? '' : 's'} deposit.
          </>
        ) : choosing ? (
          <>Choose which of the {words.units} you are applying for. Each has its own rent.</>
        ) : (
          <>
            Rent is {formatXaf(property.price)} a month with {property.depositMonths} month
            {property.depositMonths === 1 ? '' : 's'} deposit. The landlord sees your name, phone number and whatever you add
            below.
          </>
        )}{' '}
        {guest ? 'You do not need an account to apply.' : ''}
      </p>

      <form id="apply-form" className="stack" onSubmit={submit}>
        {choosing ? (
          <Field label={`Which ${words.unit}`} error={errors.unitId} required>
            <select value={selectedUnitId ?? ''} onChange={(event) => setSelectedUnitId(event.target.value || null)} required>
              <option value="">Choose one</option>
              {freeUnits.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.label} - {formatXaf(unit.rent)} a month, {unit.rooms} room{unit.rooms === 1 ? '' : 's'}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        {guest ? (
          <fieldset className="fieldset">
            <legend>Who you are</legend>
            <p className="field-hint">
              The landlord needs a way to reach you, and this is where your sign-in details go if they
              approve you. Already have an account? <Link to="/login">Sign in first</Link>.
            </p>

            <Field label="Full name" error={errors.name} required>
              <input
                type="text"
                autoComplete="name"
                required
                value={form.name}
                onChange={(event) => set({ name: event.target.value })}
                placeholder="Ngwa Emmanuel"
              />
            </Field>

            <div className="grid-2">
              <Field label="Email" hint="Your username, if you are approved." error={errors.email} required>
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={form.email}
                  onChange={(event) => set({ email: event.target.value })}
                />
              </Field>

              <Field label="Phone number" error={errors.phone} required>
                <input
                  type="tel"
                  autoComplete="tel"
                  required
                  value={form.phone}
                  onChange={(event) => set({ phone: event.target.value })}
                  placeholder="+237 6 70 00 00 00"
                />
              </Field>
            </div>
          </fieldset>
        ) : null}

        <div className="grid-2">
          <Field label="How many people will live there" error={errors.occupants} required>
            <input
              type="number"
              min="1"
              max="30"
              value={form.occupants}
              onChange={(event) => set({ occupants: event.target.value })}
            />
          </Field>

          <Field label="When do you want to move in" error={errors.moveInDate}>
            <input type="date" value={form.moveInDate} onChange={(event) => set({ moveInDate: event.target.value })} />
          </Field>

          <Field label="Monthly income in FCFA" hint="Optional, but it helps your case." error={errors.monthlyIncome}>
            <input
              type="number"
              min="0"
              step="1"
              value={form.monthlyIncome}
              onChange={(event) => set({ monthlyIncome: event.target.value })}
              placeholder="150000"
            />
          </Field>

          <Field label="What you do" error={errors.employment}>
            <input
              type="text"
              value={form.employment}
              onChange={(event) => set({ employment: event.target.value })}
              placeholder="Teacher, Government School Tiko"
            />
          </Field>
        </div>

        <Field label="Message to the landlord" error={errors.coverMessage}>
          <textarea
            rows="4"
            value={form.coverMessage}
            onChange={(event) => set({ coverMessage: event.target.value })}
            placeholder="Introduce yourself in a line or two and say when you can come and see the house."
          />
        </Field>

        {/* Uploading needs an account to hang the files on. A guest is told when
            they can add them rather than being shown a control that would fail. */}
        {guest ? (
          <p className="field-hint">
            You can attach your ID and proof of income once you are approved and have signed in.
          </p>
        ) : (
        <fieldset className="fieldset">
          <legend>Supporting documents</legend>
          <p className="field-hint">
            A national ID and proof of income are what most landlords ask for. PDF or a clear photo, up to 5 files.
          </p>
          <div className="grid-2">
            <Field label="Document type">
              <select value={documentKind} onChange={(event) => setDocumentKind(event.target.value)}>
                {documentKinds.map((kind) => (
                  <option key={kind.slug} value={kind.slug}>
                    {kind.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Choose files">
              <input
                type="file"
                multiple
                accept="image/*,application/pdf"
                onChange={(event) => setDocuments([...event.target.files].slice(0, 5))}
              />
            </Field>
          </div>
          {documents.length > 0 ? (
            <ul className="file-list">
              {documents.map((file) => (
                <li key={file.name}>
                  {file.name} <span className="muted">({Math.round(file.size / 1024)} KB)</span>
                </li>
              ))}
            </ul>
          ) : null}
        </fieldset>
        )}
      </form>
    </Modal>
  );
};

/**
 * Offering to buy. A figure, how the buyer means to pay and a note for the
 * seller - nothing is paid here, and the modal says so.
 */
const OfferModal = ({ property, onClose, onDone }) => {
  const toast = useToast();
  const { buyerFinance } = useMeta();
  const [form, setForm] = useState({ amount: String(property.price || ''), finance: 'cash', message: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const amount = Number(form.amount) || 0;
  const gap = property.price ? amount - property.price : 0;

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await api.post(`/properties/${property.id}/offers`, {
        amount,
        finance: form.finance,
        message: form.message.trim() || null
      });
      toast.success('Offer sent. The seller has been told, and you will hear when they reply.');
      onDone();
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Make an offer for ${property.title}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="offer-form" className="btn btn-primary" disabled={busy || amount < 1000}>
            {busy ? 'Sending...' : 'Send offer'}
          </button>
        </>
      }
    >
      <p className="modal-lead">
        The asking price is {formatXaf(property.price)}
        {property.negotiable ? ', and the seller says it is negotiable' : ''}. Nothing is paid here: if the seller
        accepts, you meet, check the papers and complete the sale before a notary.
      </p>
      <form id="offer-form" className="stack" onSubmit={submit}>
        <div className="grid-2">
          <Field
            label="Your offer in FCFA"
            hint={
              amount && property.price
                ? gap === 0
                  ? 'The asking price.'
                  : `${formatXaf(Math.abs(gap))} ${gap < 0 ? 'below' : 'above'} the asking price.`
                : undefined
            }
            error={errors.amount}
            required
          >
            <input type="number" min="1000" step="1" value={form.amount} onChange={(event) => set({ amount: event.target.value })} required />
          </Field>
          <Field label="How you will pay" error={errors.finance}>
            <select value={form.finance} onChange={(event) => set({ finance: event.target.value })}>
              {buyerFinance.map((item) => (
                <option key={item.slug} value={item.slug}>
                  {item.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="A note for the seller" hint="When you could view it, or a question about the papers." error={errors.message}>
          <textarea rows="4" value={form.message} onChange={(event) => set({ message: event.target.value })} />
        </Field>
      </form>
    </Modal>
  );
};

const MessageModal = ({ property, onClose }) => {
  const toast = useToast();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post('/messages', { propertyId: property.id, body });
      toast.success('Message sent. The reply will show up under Messages.');
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Message ${property.landlord?.businessName || property.landlord?.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="message-form" className="btn btn-primary" disabled={busy || !body.trim()}>
            {busy ? 'Sending...' : 'Send message'}
          </button>
        </>
      }
    >
      <form id="message-form" onSubmit={send}>
        <Field label="Your message">
          <textarea
            rows="5"
            autoFocus
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder={
              isForSale(property)
                ? 'Good day. Is this still for sale, and can I see the land certificate and the site plan?'
                : 'Good day. Is this house still available, and can I come and see it on Saturday?'
            }
          />
        </Field>
      </form>
    </Modal>
  );
};

const ReportModal = ({ property, onClose }) => {
  const toast = useToast();
  const { reportReasons } = useMeta();
  const [reason, setReason] = useState(reportReasons[0]?.slug || 'other');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post('/reports', { propertyId: property.id, reason, details: details || undefined });
      toast.success('Thank you. An administrator will look at this listing.');
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Report this listing"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="report-form" className="btn btn-danger" disabled={busy}>
            {busy ? 'Sending...' : 'Send report'}
          </button>
        </>
      }
    >
      <form id="report-form" className="stack" onSubmit={submit}>
        <Field label="What is wrong">
          <select value={reason} onChange={(event) => setReason(event.target.value)}>
            {reportReasons.map((item) => (
              <option key={item.slug} value={item.slug}>
                {item.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Anything else we should know">
          <textarea rows="4" value={details} onChange={(event) => setDetails(event.target.value)} />
        </Field>
      </form>
    </Modal>
  );
};

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { amenityLabel, propertyTypeLabel, landTitleLabel, landUseLabel } = useMeta();
  const toast = useToast();

  const { data, loading, error, reload, setData } = useFetch(`/properties/${id}`, [id]);
  const [dialog, setDialog] = useState(null);
  const [selectedUnitId, setSelectedUnitId] = useState(null);
  const [basemap, setBasemap] = useState('map');
  // Whether this buyer already has an offer in; fetched for any signed-in visitor.
  const myOffers = useFetch(user ? '/offers/mine' : null, [user?.id]);

  useTitle(data?.property?.title);

  if (loading && !data) return <Spinner label="Loading the listing" />;
  if (error) return <div className="container section"><ErrorState message={error} onRetry={reload} /></div>;
  if (!data) return null;

  const { property, myApplication, similar } = data;
  const place = [property.neighbourhood, property.city].filter(Boolean).join(', ');
  const isOwner = user?.id === property.landlordId;
  const canApply = !user || (user.role === 'tenant' && !isOwner);
  const alreadyApplied = myApplication && myApplication.status !== 'withdrawn';

  const toggleFavorite = async () => {
    if (!user) {
      toast.info('Sign in to save this house to your list.');
      navigate('/login', { state: { from: { pathname: `/properties/${id}` } } });
      return;
    }
    const next = !property.favorited;
    setData({ ...data, property: { ...property, favorited: next } });
    try {
      if (next) await api.post(`/favorites/${property.id}`);
      else await api.del(`/favorites/${property.id}`);
    } catch (requestError) {
      setData({ ...data, property: { ...property, favorited: !next } });
      toast.error(requestError.message);
    }
  };

  // Applying is open to anyone signed out. Messaging still needs an account, because
  // a thread has to belong to somebody who can come back and read the reply.
  const requireAccountThen = (action) => {
    if (!user && action !== 'apply') {
      navigate(`/register?role=tenant&next=/properties/${property.id}`);
      return;
    }
    // A landlord or administrator signed in on their own account has nowhere to
    // track an application, and the API would refuse it anyway. Say so here rather
    // than opening a form that cannot be submitted.
    if (action === 'apply' && user && user.role !== 'tenant') {
      toast.info('Applications are made from a tenant account. Sign out to apply for this house.');
      return;
    }
    if (action === 'offer' && !['tenant', 'landlord'].includes(user.role)) {
      toast.info('Offers are made from a personal account, not a manager or administrator one.');
      return;
    }
    setDialog(action);
  };

  const sale = isForSale(property);
  const badge = statusBadgeFor(property);
  const myOffer = (myOffers.data?.items || []).find(
    (offer) => offer.propertyId === property.id && ['submitted', 'accepted'].includes(offer.status)
  );
  const words = lettingOf(property);
  const multi = !sale && isMultiUnit(property) && property.units?.length > 0;
  const units = property.units || [];
  const freeUnits = units.filter((unit) => unitAvailability(unit) === 'available');
  const cheapestFree = freeUnits.reduce((low, unit) => (low === null || unit.rent < low ? unit.rent : low), null);
  const cheapest = units.reduce((low, unit) => (low === null || unit.rent < low ? unit.rent : low), null);
  // The rent the money table works from: the cheapest free unit in a building.
  const headlineRent = multi ? cheapestFree ?? cheapest ?? property.price : property.price;
  const availability = property.availability || (property.status === 'rented' ? 'occupied' : property.status);

  const openApply = (unitId = null) => {
    setSelectedUnitId(unitId);
    requireAccountThen('apply');
  };

  return (
    <div className="detail-page">
      <div className="container">
        <nav className="breadcrumbs" aria-label="Breadcrumb">
          <Link to={sale ? '/search?kind=sale' : '/search'}>{sale ? 'For sale' : 'Houses to rent'}</Link>
          <span aria-hidden="true">/</span>
          <Link to={`/search?city=${property.city}${sale ? '&kind=sale' : ''}`}>{property.city}</Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">{property.title}</span>
        </nav>

        {property.moderationStatus !== 'approved' ? (
          <div className={`notice notice-${property.moderationStatus === 'rejected' ? 'bad' : 'warn'}`}>
            <strong>
              {property.moderationStatus === 'rejected' ? 'This listing was rejected.' : 'This listing is waiting for review.'}
            </strong>
            {property.moderationNote ? <span> {property.moderationNote}</span> : null}
            <span> Only you and the administrators can see this page.</span>
          </div>
        ) : null}

        {property.offMarket ? (
          <div className="notice notice-info">
            <strong>Taken off the market by the owner.</strong> It is not {sale ? 'for sale' : 'available'} just now, so it
            cannot be {sale ? 'offered for' : 'applied for'}. Its history is below, and it may come back on.
          </div>
        ) : null}

        <Gallery media={property.media || []} title={property.title} />

        <div className="detail-layout">
          <div className="detail-main">
            <header className="detail-head">
              <div>
                {sale ? (
                  <p className="detail-price">
                    {formatXaf(property.price)}
                    {property.negotiable ? <span className="per-month"> - negotiable</span> : null}
                  </p>
                ) : (
                  <p className="detail-price">
                    {multi ? <span className="per-month">from </span> : null}
                    {formatXaf(headlineRent)}
                    <span className="per-month"> / month</span>
                  </p>
                )}
                <h1>{property.title}</h1>
                <p className="detail-place">{place}</p>
              </div>

              <div className="detail-head-actions">
                <StatusBadge status={badge.status} label={badge.label} />
                <button
                  type="button"
                  className={`btn btn-outline btn-sm ${property.favorited ? 'is-saved' : ''}`}
                  onClick={toggleFavorite}
                >
                  {property.favorited ? 'Saved' : 'Save'}
                </button>
              </div>
            </header>

            {sale ? (
              <ul className="fact-strip">
                <li>
                  <strong>{propertyTypeLabel(property.propertyType)}</strong>
                  <span>Type</span>
                </li>
                <li>
                  <strong>{formatPlot(property.plotSqm) || '-'}</strong>
                  <span>Plot size</span>
                </li>
                <li>
                  <strong>{landUseLabel(property.landUse) || '-'}</strong>
                  <span>Land use</span>
                </li>
                <li className={property.landTitle === 'titled' ? 'is-good' : ''}>
                  <strong>{landTitleLabel(property.landTitle) || 'Not stated'}</strong>
                  <span>Land title</span>
                </li>
                {property.propertyType !== 'land' ? (
                  <li>
                    <strong>{property.rooms === 0 ? '-' : property.rooms}</strong>
                    <span>{property.rooms === 1 ? 'Room' : 'Rooms'}</span>
                  </li>
                ) : null}
              </ul>
            ) : (
              <ul className="fact-strip">
                {multi ? (
                  <>
                    <li>
                      <strong>{units.length}</strong>
                      <span>{words.units.charAt(0).toUpperCase() + words.units.slice(1)}</span>
                    </li>
                    <li>
                      <strong>{freeUnits.length}</strong>
                      <span>Available now</span>
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      <strong>{property.rooms === 0 ? '-' : property.rooms}</strong>
                      <span>{property.rooms === 1 ? 'Room' : 'Rooms'}</span>
                    </li>
                    <li>
                      <strong>{property.bathrooms}</strong>
                      <span>{property.bathrooms === 1 ? 'Bathroom' : 'Bathrooms'}</span>
                    </li>
                  </>
                )}
                {property.areaSqm ? (
                  <li>
                    <strong>{formatNumber(property.areaSqm)}</strong>
                    <span>Square metres</span>
                  </li>
                ) : null}
                <li>
                  <strong>{property.depositMonths}</strong>
                  <span>Months deposit</span>
                </li>
                <li>
                  <strong>{multi ? words.short : propertyTypeLabel(property.propertyType)}</strong>
                  <span>Type</span>
                </li>
                <li>
                  <strong>{property.furnished ? 'Yes' : 'No'}</strong>
                  <span>Furnished</span>
                </li>
              </ul>
            )}

            <section className="detail-section">
              <h2>About this place</h2>
              <p className="detail-description">{property.description || 'The landlord has not written a description yet.'}</p>
            </section>

            {property.amenities?.length ? (
              <section className="detail-section">
                <h2>What you get</h2>
                <ul className="amenity-list">
                  {property.amenities.map((slug) => (
                    <li key={slug}>{amenityLabel(slug)}</li>
                  ))}
                </ul>
              </section>
            ) : null}

            {units.length ? (
              <section className="detail-section" id="units">
                <h2>
                  {multi
                    ? `${words.units.charAt(0).toUpperCase() + words.units.slice(1)} in this ${property.letting === 'rooms' ? 'house' : 'building'}`
                    : 'Units in this property'}
                </h2>
                <p className="field-hint">
                  {freeUnits.length > 0
                    ? `${freeUnits.length} of ${units.length} available now. Each has its own rent - apply for the one you want.`
                    : `All ${units.length} are taken right now. Save the listing and check back: they come free as leases end.`}
                </p>
                <div className="unit-grid">
                  {units.map((unit) => {
                    const where = unitAvailability(unit);
                    return (
                      <article key={unit.id} className={`unit-card is-${where}`}>
                        <div className="unit-card-head">
                          <strong>{unit.label}</strong>
                          <StatusBadge status={where} label={AVAILABILITY_LABEL[where]} />
                        </div>
                        <p className="unit-card-rent">
                          {formatXaf(unit.rent)}
                          <span className="per-month"> / month</span>
                        </p>
                        <p className="muted">
                          {unit.rooms} {unit.rooms === 1 ? 'room' : 'rooms'} - {unit.bathrooms}{' '}
                          {unit.bathrooms === 1 ? 'bathroom' : 'bathrooms'}
                          {unit.areaSqm ? ` - ${unit.areaSqm} m2` : ''}
                          {unit.furnished ? ' - furnished' : ''}
                        </p>
                        <p className="muted">
                          {unit.depositMonths} month{unit.depositMonths === 1 ? '' : 's'} deposit
                        </p>
                        {isOwner ? null : (
                          <button
                            type="button"
                            className={`btn btn-sm ${where === 'available' ? 'btn-primary' : 'btn-outline'}`}
                            onClick={() => openApply(unit.id)}
                            disabled={!canApply || where !== 'available' || alreadyApplied || property.offMarket}
                          >
                            {where === 'available'
                              ? `Apply for ${unit.label}`
                              : where === 'pending'
                                ? 'Somebody is taking it'
                                : where === 'occupied'
                                  ? 'Rented'
                                  : 'Not available'}
                          </button>
                        )}
                      </article>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {property.history?.length ? (
              <section className="detail-section">
                <div className="section-head">
                  <h2>Listing history</h2>
                  {sale && property.parcel ? (
                    <Link to={`/land/${property.parcel.id}`} className="link-strong">
                      The land's full history
                    </Link>
                  ) : null}
                </div>
                <ol className="timeline">
                  {property.history.map((event, index) => (
                    <li
                      key={`${event.at}-${index}`}
                      className={`is-${{ listed: 'info', relisted: 'good', delisted: 'warn', pending: 'warn', closed: 'good', available: 'good', price: 'info' }[event.kind] || 'info'}`}
                    >
                      <span className="timeline-date">{formatDate(event.at)}</span>
                      <span className="timeline-what">
                        {event.text}
                        {event.kind === 'price'
                          ? ` from ${formatXaf(event.from)} to ${formatXaf(event.to)}`
                          : event.price && ['listed', 'relisted'].includes(event.kind)
                            ? ` at ${formatXaf(event.price)}${sale ? '' : ' a month'}`
                            : ''}
                      </span>
                    </li>
                  ))}
                </ol>
              </section>
            ) : null}

            <section className="detail-section">
              <div className="section-head">
                <h2>Where it is</h2>
                <div className="view-toggle" role="group" aria-label="Map style">
                  {[
                    ['map', 'Map'],
                    ['satellite', 'Satellite']
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={basemap === value ? 'is-current' : ''}
                      onClick={() => setBasemap(value)}
                      aria-pressed={basemap === value}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <p className="muted">
                {property.address ? `${property.address}. ` : ''}
                The pin shows the neighbourhood. The landlord will give you the exact directions when you arrange a
                viewing.
              </p>
              <MapPanel points={[property]} single height={320} basemap={basemap} />
            </section>

            {sale ? (
              <>
                {property.parcel ? (
                  <section className="detail-section">
                    <div className="section-head">
                      <h2>The plot on the land register</h2>
                      <Link to={`/land/${property.parcel.id}`} className="link-strong">
                        See its history
                      </Link>
                    </div>
                    <ParcelMap height={300} plots={[{ id: property.parcel.id, boundary: property.parcel.boundary, tone: 'plot', label: property.parcel.reference }]} />
                    <p className="field-hint">
                      {property.parcel.reference}, {plotSize(property.parcel.areaSqm)} in {property.parcel.village}
                      {property.parcel.plotNumber ? `, plot ${property.parcel.plotNumber}` : ''}. The boundary comes from the
                      site plan signed by the village chief and was checked by our team. Only the registered owner can sell it.
                    </p>
                  </section>
                ) : null}

                <section className="detail-section">
                  <h2>Site plan</h2>
                  {property.sitePlanUrl ? (
                    property.sitePlanUrl.toLowerCase().endsWith('.pdf') ? (
                      <a className="btn btn-outline" href={property.sitePlanUrl} target="_blank" rel="noreferrer">
                        Open the site plan (PDF)
                      </a>
                    ) : (
                      <a href={property.sitePlanUrl} target="_blank" rel="noreferrer" className="site-plan-preview">
                        <img src={property.sitePlanUrl} alt={`Site plan for ${property.title}`} />
                      </a>
                    )
                  ) : (
                    <p className="muted">The seller has not put up a site plan yet. Ask for it before you go any further.</p>
                  )}
                </section>

                <section className="detail-section">
                  <h2>Buying safely</h2>
                  <ul className="safety-list">
                    <li>See the land and walk the boundary with the seller before any money changes hands.</li>
                    <li>
                      Ask for the land certificate (titre foncier) and check it at the land registry (Conservation
                      Fonciere) for the area.
                    </li>
                    <li>Match the plot number and the boundary beacons on the ground to the site plan.</li>
                    <li>Complete the sale before a notary, and pay into an account, not in cash at the plot.</li>
                  </ul>
                  <p className="field-hint">Report any seller who asks for a deposit before showing you the papers.</p>
                </section>
              </>
            ) : (
              <section className="detail-section">
                <h2>Money up front</h2>
                <table className="cost-table">
                  <tbody>
                    <tr>
                      <th scope="row">Yearly rent (12 months)</th>
                      <td>{formatXaf(headlineRent * 12)}</td>
                    </tr>
                    <tr>
                      <th scope="row">
                        Deposit ({property.depositMonths} month{property.depositMonths === 1 ? '' : 's'})
                      </th>
                      <td>{formatXaf(headlineRent * property.depositMonths)}</td>
                    </tr>
                    <tr className="total-row">
                      <th scope="row">Total to move in</th>
                      <td>{formatXaf(headlineRent * 12 + headlineRent * property.depositMonths)}</td>
                    </tr>
                  </tbody>
                </table>
                <p className="field-hint">
                  {multi ? `Worked out for the cheapest ${words.unit} that is free; each has its own rent above. ` : ''}
                  Never send money before you have seen the house and met the landlord. Report anyone who asks.
                </p>
              </section>
            )}
          </div>

          <aside className="detail-side">
            <div className="side-card">
              {property.landlord ? (
                <>
                  <div className="landlord-row">
                    <Avatar name={property.landlord.name} url={property.landlord.avatarUrl} size={52} />
                    <div>
                      <strong>{property.landlord.businessName || property.landlord.name}</strong>
                      <span className="muted">
                        {property.landlord.businessName ? property.landlord.name : sale ? 'Seller' : 'Landlord'}
                        {property.landlord.verified ? ' - verified' : ''}
                      </span>
                      <Link className="link-small" to={`/landlords/${property.landlord.id}`}>
                        See their other listings
                      </Link>
                    </div>
                  </div>
                  {property.landlord.verified ? (
                    <p className="verified-note">This landlord has had their identity checked by our team.</p>
                  ) : (
                    <p className="unverified-note">
                      This landlord has not completed identity checks yet. Take the usual care.
                    </p>
                  )}
                </>
              ) : null}

              {isOwner ? (
                <div className="stack">
                  <Link to={`/landlord/properties/${property.id}/edit`} className="btn btn-primary btn-block">
                    Edit this listing
                  </Link>
                  {sale ? (
                    <Link to="/landlord/offers" className="btn btn-outline btn-block">
                      See offers
                    </Link>
                  ) : (
                    <Link to={`/landlord/applications?propertyId=${property.id}`} className="btn btn-outline btn-block">
                      See applications
                    </Link>
                  )}
                </div>
              ) : property.offMarket ? (
                <div className="stack">
                  <p className="notice notice-info">Off the market for now.</p>
                  <Link to={`/search?city=${property.city}${sale ? '&kind=sale' : ''}`} className="btn btn-outline btn-block">
                    See what else is in {property.city}
                  </Link>
                </div>
              ) : sale ? (
                <div className="stack">
                  {availability === 'occupied' ? (
                    <p className="notice notice-info">This has been sold.</p>
                  ) : myOffer ? (
                    <div className="applied-box">
                      <StatusBadge
                        status={myOffer.status === 'accepted' ? 'approved' : 'submitted'}
                        label={myOffer.status === 'accepted' ? 'Offer accepted' : 'Offer sent'}
                      />
                      <p>
                        You offered {formatXaf(myOffer.amount)} {timeAgo(myOffer.createdAt)}.
                        {myOffer.status === 'accepted' ? ' Arrange the next steps with the seller.' : ' The seller has not replied yet.'}
                      </p>
                      <Link to="/my/offers" className="link-strong">
                        See my offers
                      </Link>
                    </div>
                  ) : (
                    <>
                      {availability === 'pending' ? (
                        <p className="notice notice-warn">
                          Under offer: the seller has accepted an offer. You can still make one in case it falls through.
                        </p>
                      ) : null}
                      <button type="button" className="btn btn-primary btn-block btn-lg" onClick={() => requireAccountThen('offer')}>
                        Make an offer
                      </button>
                    </>
                  )}
                  <button type="button" className="btn btn-outline btn-block" onClick={() => requireAccountThen('message')}>
                    Message the seller
                  </button>
                  {property.landlord?.phone && user ? (
                    <a className="btn btn-ghost btn-block" href={`tel:${property.landlord.phone}`}>
                      Call {property.landlord.phone}
                    </a>
                  ) : null}
                </div>
              ) : (multi ? freeUnits.length === 0 : availability === 'occupied') ? (
                <div className="stack">
                  <p className="notice notice-info">
                    {multi
                      ? `Every ${words.unit} here is taken right now. Save the listing and check back.`
                      : 'This house is rented - somebody is already living in it.'}
                  </p>
                  <Link to={`/search?city=${property.city}`} className="btn btn-outline btn-block">
                    See other houses in {property.city}
                  </Link>
                </div>
              ) : (
                <div className="stack">
                  {alreadyApplied ? (
                    <div className="applied-box">
                      <StatusBadge status={myApplication.status} />
                      <p>
                        You applied {timeAgo(myApplication.createdAt)}. {myApplication.decisionNote || ''}
                      </p>
                      <Link to="/tenant/applications" className="link-strong">
                        Track this application
                      </Link>
                    </div>
                  ) : (
                    <>
                      {/* Applying is the point of the page, so it is the primary action
                          for everyone who is not the owner. A landlord or administrator
                          gets told why they cannot rather than finding no button. */}
                      {!multi && availability === 'pending' ? (
                        <p className="notice notice-warn">
                          Pending: somebody is in the process of taking this house. You can still apply in case it falls
                          through.
                        </p>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-primary btn-block btn-lg"
                        onClick={() => openApply()}
                      >
                        {multi ? `Choose ${words.unit === 'apartment' ? 'an' : 'a'} ${words.unit} and apply` : 'Apply for property'}
                      </button>
                      {multi ? (
                        <p className="field-hint">
                          {freeUnits.length} of {units.length} {words.units} available, from {formatXaf(cheapestFree)}.
                        </p>
                      ) : null}
                      {!canApply ? (
                        <p className="field-hint">
                          You are signed in as a {user.role}. Applications are made from a tenant
                          account.
                        </p>
                      ) : null}
                    </>
                  )}

                  <button type="button" className="btn btn-outline btn-block" onClick={() => requireAccountThen('message')}>
                    Message the landlord
                  </button>

                  {property.landlord?.phone && user ? (
                    <a className="btn btn-ghost btn-block" href={`tel:${property.landlord.phone}`}>
                      Call {property.landlord.phone}
                    </a>
                  ) : null}
                </div>
              )}

              <dl className="side-meta">
                <div>
                  <dt>Listed</dt>
                  <dd>{formatDate(property.createdAt)}</dd>
                </div>
                <div>
                  <dt>Views</dt>
                  <dd>{formatNumber(property.viewCount)}</dd>
                </div>
                <div>
                  <dt>Reference</dt>
                  <dd>NDK-{String(property.id).padStart(5, '0')}</dd>
                </div>
              </dl>

              {!isOwner ? (
                <button type="button" className="link-btn danger-link" onClick={() => requireAccountThen('report')}>
                  Report this listing
                </button>
              ) : null}
            </div>
          </aside>
        </div>

        {similar?.length ? (
          <section className="section">
            <div className="section-head">
              <h2>{sale ? `More for sale in ${property.city}` : `Similar houses in ${property.city}`}</h2>
            </div>
            <div className="card-grid">
              {similar.map((item) => (
                <PropertyCard key={item.id} property={item} compact />
              ))}
            </div>
          </section>
        ) : null}
      </div>

      {dialog === 'apply' ? (
        <ApplyModal
          property={property}
          initialUnitId={selectedUnitId}
          onClose={() => {
            setDialog(null);
            setSelectedUnitId(null);
          }}
          onDone={(application) => {
            setDialog(null);
            setSelectedUnitId(null);
            setData({ ...data, myApplication: application });
          }}
        />
      ) : null}
      {dialog === 'message' ? <MessageModal property={property} onClose={() => setDialog(null)} /> : null}
      {dialog === 'offer' ? (
        <OfferModal
          property={property}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            myOffers.reload();
          }}
        />
      ) : null}
      {dialog === 'report' ? <ReportModal property={property} onClose={() => setDialog(null)} /> : null}
    </div>
  );
};

export default PropertyDetail;
